import React from "react";
import { GetServerSideProps, NextPage } from "next";
import LeftAuthPanel from "../../components/LeftAuthPanel";
import BrimbleLogo from "../../Icons/BrimbleLogo";
import TextField from "../../components/Micro/TextField";
import Button from "../../components/Micro/Button";
import { Formik } from "formik";
import { login } from "../../backend/auth";
import { useRouter } from "next/router";
import { AuthLabel } from "../register/email";
import config from "../../config";
import validation from "../../utils/validation";
import { handleRequestError } from "../../backend";
import Link from "next/link";
import { LoginDataType } from "../../types/auth.type";
import Seo from "../../components/Micro/Seo";
import AuthLayout from "@/layouts/AuthLayout";

const LoginWithEmail: NextPage = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const router = useRouter();

  const handleLogin = (data: LoginDataType) => {
    setLoading(true);
    login(data)
      .then(() => router.push(`/login/verification?email=${data.email}`))
      .catch(handleRequestError)
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    return () => setLoading(false);
  }, []);
  return (
    <>
      <Seo
        title="Login • Email"
        description="Authenticate with email to continue using Brimble"
      />

      <AuthLayout>
        <>
          <div className="flex items-center justify-between w-full">
            <div className="space-y-2">
              <h1 className="text-[40px] text-primary-dark dark:text-white font-semibold w-2/3">
                Welcome Back
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm w-2/3">
                Log in with your email to access your dashboard and manage your
                projects.
              </p>
            </div>
            <Link href={"https://brimble.io"} passHref>
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-light dark:bg-theme-color-light">
                <BrimbleLogo />
              </div>
            </Link>
          </div>
          <Formik
            initialValues={{ email: "" }}
            validationSchema={validation.login}
            onSubmit={handleLogin}
          >
            {({
              errors,
              touched,
              values,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <div className="w-full py-10 space-y-2 overflow-x-hidden overflow-y-auto">
                <TextField
                  value={values["email"]}
                  error={Boolean(errors["email"])}
                  helperText={errors["email"]}
                  touched={touched["email"]}
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your email address"
                  label={<AuthLabel />}
                  name="email"
                  className="!input-md border-none dark:bg-theme-color-light"
                />
                <Button
                  type="submit"
                  onClick={() => handleSubmit()}
                  loading={loading}
                  className="btn-block btn-md !my-5"
                >
                  <span className="text-[14px]">login to your account</span>
                </Button>

                <button
                  onClick={() => router.push("/login")}
                  className="mt-5 text-[14px] capitalize btn-link text-primary dark:text-gray-400 btn btn-primary btn-block btn-lg no-underline"
                >
                  Use other login options
                </button>
              </div>
            )}
          </Formik>
        </>
      </AuthLayout>
    </>
  );
};

export default LoginWithEmail;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  if (req.cookies[config.ACCESS_TOKEN]) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  } else {
    return {
      props: {},
    };
  }
};
